/**
 * Check if user has a specific permission
 * @param {Array<string>} userPermissions - Array of user's permissions
 * @param {string} requiredPermission - The permission to check for
 * @returns {boolean} - True if user has permission or is admin with wildcard
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  // If no required permission specified, allow access
  if (!requiredPermission) {
    return true;
  }

  // If no user permissions, deny access
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }

  // Check for admin wildcard permission
  if (userPermissions.includes("*")) {
    return true;
  }

  // Check if user has the specific permission
  return userPermissions.includes(requiredPermission);
};

/**
 * Check if user has any of the required permissions
 * @param {Array<string>} userPermissions - Array of user's permissions
 * @param {Array<string>} requiredPermissions - Array of permissions to check for
 * @returns {boolean} - True if user has at least one of the required permissions
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  // If no required permissions specified, allow access
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  // If no user permissions, deny access
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }

  // Check for admin wildcard permission
  if (userPermissions.includes("*")) {
    return true;
  }

  // Check if user has any of the required permissions
  return requiredPermissions.some((permission) =>
    userPermissions.includes(permission),
  );
};

/**
 * Filter menu items based on user permissions
 * Recursively filters children as well
 * @param {Array} menuItems - Array of menu items to filter
 * @param {Array<string>} userPermissions - Array of user's permissions
 * @returns {Array} - Filtered menu items
 */
export const filterMenuByPermissions = (menuItems, userPermissions) => {
  if (!menuItems || !Array.isArray(menuItems)) {
    return [];
  }

  return menuItems
    .filter((item) => {
      // If item has a permission requirement, check it
      if (item.permission) {
        return hasPermission(userPermissions, item.permission);
      }
      // If item has children, check if any children are accessible
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuByPermissions(
          item.children,
          userPermissions,
        );
        return filteredChildren.length > 0;
      }
      // If no permission requirement and no children, allow access
      return true;
    })
    .map((item) => {
      // If item has children, filter them recursively
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: filterMenuByPermissions(item.children, userPermissions),
        };
      }
      return item;
    });
};
